import { useCallback, useEffect } from 'react';

import { Layer } from 'mapbox-gl';

import { useMap } from '@domain/map/MapContext';
import { useExplore } from '../../../views/Explore/context/ExploreContext';
import { clearActiveHighlights, highlightMapIcon } from '../helpers/mapHelpers';
import { HIGHLIGHT_LAYERS } from '../mapData/highlightLayers';
import {
  IMapClickEvent,
  HandleMapIconClick,
  SetSelectedMapIcon,
  LayerWithToggleableGroup,
} from '../types/types';

interface IProps {
  layer: Layer & LayerWithToggleableGroup;
  highlightLayer: keyof typeof HIGHLIGHT_LAYERS;
  handleMapIconClick: HandleMapIconClick;
}

const parseDrawerViewId = ({
  drawerViewId,
  highlightLayer,
}: {
  drawerViewId: string;
  highlightLayer: keyof typeof HIGHLIGHT_LAYERS;
}): string | number =>
  highlightLayer === 'pointsOfInterest' ? Number(drawerViewId) : drawerViewId;

export const MapIconClickHandler = ({ layer, highlightLayer, handleMapIconClick }: IProps) => {
  const { map, mapLayersLoaded } = useMap();
  const { exploreQueryParams, saveParamsInUrl } = useExplore();
  const { drawerView, drawerViewId, latitude: urlLat, longitude: urlLng } = exploreQueryParams;

  const setSelectedMapIcon: SetSelectedMapIcon = useCallback(
    newExploreQueryParams => {
      saveParamsInUrl(newExploreQueryParams);
    },
    // skip saveParamsInUrl to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isolateMapClickEvent = useCallback(
    (event: IMapClickEvent) => {
      const features = map!.queryRenderedFeatures(event.point);
      const { source } = features[0]!;
      // composite is source of all city labels; we can ignore it
      if (source === 'composite' || source === layer.source) {
        handleMapIconClick({
          layerId: layer.id,
          toggleableGroups: layer.toggleableGroups,
          event,
          map: map!,
          setSelectedIcon: setSelectedMapIcon,
        });
      }
    },
    [handleMapIconClick, layer.id, layer.source, layer.toggleableGroups, map, setSelectedMapIcon],
  );

  const changeCursor = useCallback(
    (cursor: string) => () => {
      map!.getCanvas().style.cursor = cursor;
    },
    [map],
  );

  useEffect(() => {
    if (map && mapLayersLoaded) {
      if (
        drawerView &&
        drawerViewId &&
        drawerView === HIGHLIGHT_LAYERS[highlightLayer].drawerView
      ) {
        clearActiveHighlights(map);
        highlightMapIcon({
          map,
          highlightLayer,
          value: parseDrawerViewId({ drawerViewId, highlightLayer }),
          layer,
        });
        if (urlLat !== undefined && urlLng !== undefined) {
          map.easeTo({ center: { lat: urlLat, lon: urlLng } });
        }
      }

      if (!drawerViewId) {
        clearActiveHighlights(map);
      }
    }
    // skip urlLat and urlLng to avoid running on map scrolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapLayersLoaded, drawerView, drawerViewId, highlightLayer, layer]);

  // handles map icon clicks
  useEffect(() => {
    if (map && mapLayersLoaded) {
      map.on('click', layer.id, isolateMapClickEvent);
      map.on('mouseenter', layer.id, changeCursor('pointer'));
      map.on('mouseleave', layer.id, changeCursor(''));
    }

    return () => {
      if (map) {
        map.off('click', layer.id, isolateMapClickEvent);
        map.off('mouseenter', layer.id, changeCursor('pointer'));
        map.off('mouseleave', layer.id, changeCursor(''));
      }
    };
  }, [changeCursor, isolateMapClickEvent, layer, map, mapLayersLoaded]);

  return <></>;
};
